import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { catchError, map, publishReplay, refCount, share, tap } from 'rxjs/operators';

import { NgbModal } from '@ng-bootstrap/ng-bootstrap';

import { Observable } from 'rxjs';
import { AppConfig } from '../../app.config';

import { UserService } from '../../core/services/user.service';
import { GameElementCreateModalComponent } from '../components/modals/game-element-create-modal/game-element-create-modal.component';
import { GameElementListModalComponent } from '../components/modals/game-element-list-modal/game-element-list-modal.component';
import { BaseFilesModel } from '../models/base-files.model';
import { GameElement } from '../models/game-element.model';
import { GameLevel } from '../models/game-level.model';

import { Status } from '../models/status.model';

import { MessageModalComponent } from '../../core/components/message-modal/message-modal.component';

import { ApiBaseService } from '../../core/services/api-base.service';
import Utils from '../utils/utils';
import { FileService } from './file.service';

import { CreateOptions } from '../interfaces/create-options.interface';
import { ModelMapperService } from './mapper/model-mapper.service';

@Injectable()
export class GameElementService extends ApiBaseService<GameElement> {

  constructor(
    protected http: HttpClient,
    protected router: Router,
    protected userService: UserService,
    protected fileService: FileService,
    protected modalService: NgbModal,
    private modelMapper: ModelMapperService
  ) {
    super(GameElement, 'elements', http, router, userService, fileService);

    this.url = AppConfig.GAME_SERVER + AppConfig.GAME_BASE_URL + '/element';
  }

  select(exclude: string[] = [], elementTypes?: number[]): Promise<GameElement[]> {
    const modalRef = this.modalService.open(GameElementListModalComponent, {size: 'lg'});
    modalRef.componentInstance.status = new Status();

    this.getAll(modalRef.componentInstance.status)
      .subscribe((elements: GameElement[]) => {
        console.log(`GameElementService#select [${exclude}] [${JSON.stringify(elements)}]`)
        try {
          modalRef.componentInstance.elements = elements
            .filter(element => exclude.findIndex(myGameElementId => element.id === myGameElementId) < 0);

          if (elementTypes && elementTypes.length > 0) {
            modalRef.componentInstance.elementTypes = elementTypes;
          }
        } catch (e) {
        }
      });

    return modalRef.result;
  }

  create(options: CreateOptions<GameElement>, nextIndex: number = 0, elementTypes?: number[], levelTypes?: number[]): Promise<GameElement> {
    return super.create(Object.assign(options, {
      component: GameElementCreateModalComponent,
      componentOptions: {
        element: this.emptyModel(),
        nextIndex,
        elementTypes,
        levelTypes
      }
    }));
  }

  getAll(status?: Status, params?: Array<{ param: string, value: string }>, path?: string): Observable<GameElement[]> {
    status = status || new Status();
    status.setLoading();

    const url = path ? AppConfig.GAME_SERVER + AppConfig.GAME_BASE_URL + path : this.url;

    let queryParams = new HttpParams();

    if (params) {
      params.forEach(param => queryParams = queryParams.append(param.param, param.value));
    }

    return this.http
      .get(url, { params: queryParams }).pipe(
        share(),
        publishReplay(1),
        refCount(),
        map(response => {
          if (response && Array.isArray(response) && response.length > 0) {
            status.setSuccess();
            return response
              .filter(object => this.checkJSON(object))
              .map(object => this.fromJSON(object));
          } else {
            status.setNoResults();
            return [];
          }
        }),
        catchError(err => this.handleError(err, status)));
  }

  update(model: any, status?: Status): Observable<any> {
    status = status || new Status();
    status.setLoading();

    const url = `${this.url}/${model.id}`;

    if (this.userService) {
      this.userService.addCurrentUser(model);
    }

    return this.http[ Utils.isValidId(model.id) ? 'put' : 'post' ](
      url,
      model.toJSON()
    ).pipe(
      map(response => {

        if (model.id || this.checkJSON(response)) {
          status.setSuccess();
          return model.id ? model : this.fromJSON(response);
        } else {
          status.setError();
        }
      }),
      tap(() => {
        if (this.fileService && model instanceof BaseFilesModel
          && model.filesToDelete && model.filesToDelete.length > 0) {
          for (const file of model.filesToDelete) {
            this.fileService.delete(file.href, true)
              .then(() => {
              }, () => {
              });
          }
        }
      }),
      catchError(err => this.handleError(err, status)),);
  }

  delete(id: string, status?: Status): Promise<any> {
    const modalRef = this.modalService.open(MessageModalComponent);
    modalRef.componentInstance.confirm = true;
    modalRef.componentInstance.message = 'Are you sure that you want to delete the element?';

    return modalRef.result.then(() => super.delete(id, status), () => Promise.reject('Dimissed'));
  }

  checkJSON(json: any): boolean {
    return this.modelMapper.gameElementCheckJSON(json);
  }

  emptyModel(): GameElement {
    return this.modelMapper.gameElementEmptyModel();
  }

  fromJSON(json: any, skipCheck?: boolean): GameElement {
    return this.modelMapper.gameElementFromJSON(json, skipCheck);
  }
}
